// =============================================================================
// Resource Type Delete Dialog - Conferma eliminazione con controllo dipendenze
// =============================================================================

import { AlertTriangle, Layers, Package, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDeleteResourceType, useResourceTypeUsage } from '../../api/hooks';
import { useTranslatedError } from '../../hooks/useTranslatedError';
import { useToast } from '../../store/toastStore';
import { ResourceType } from '../../types';
import { Badge, Button, ErrorBlock, LoadingBlock } from './_shared';

interface Props {
  resourceType: ResourceType;
  onClose: () => void;
}

/**
 * Prima di eliminare un tipo di risorsa interroga `/resources/types/:id/usage`:
 * se esistono risorse di quel tipo o servizi che lo richiedono, l'eliminazione
 * viene bloccata e la lista viene mostrata all'utente. Il backend ripete lo
 * stesso controllo (409), questo è solo il pre-check per la UI.
 */
export function ResourceTypeDeleteDialog({ resourceType, onClose }: Props) {
  const { t } = useTranslation('settings');
  const { getErrorMessage } = useTranslatedError();
  const toast = useToast();

  const { data: usage, isLoading, error } = useResourceTypeUsage(resourceType.id);
  const deleteResourceType = useDeleteResourceType();

  const isBlocked = Boolean(usage && !usage.canDelete);

  const handleDelete = async () => {
    try {
      await deleteResourceType.mutateAsync(resourceType.id);
      toast.success(t('resourceTypes.delete.successTitle', { name: resourceType.name }));
      onClose();
    } catch (err) {
      toast.error(
        t('resourceTypes.delete.errorTitle'),
        getErrorMessage(err, t('resourceTypes.delete.errorTitle')),
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isBlocked ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-600'
              }`}
            >
              <AlertTriangle className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 truncate">
                {isBlocked
                  ? t('resourceTypes.delete.blockedTitle', { name: resourceType.name })
                  : t('resourceTypes.delete.title')}
              </h2>
              <p className="text-sm text-gray-500">{resourceType.code}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label={t('common:buttons.close')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto">
          {isLoading && <LoadingBlock label={t('resourceTypes.delete.checking')} />}

          {!isLoading && error && <ErrorBlock message={t('resourceTypes.delete.checkError')} />}

          {usage && usage.canDelete && (
            <p className="text-sm text-gray-600">
              {t('resourceTypes.delete.confirmQuestion', { name: resourceType.name })}
            </p>
          )}

          {usage && !usage.canDelete && (
            <div className="space-y-5">
              <p className="text-sm text-gray-600">{t('resourceTypes.delete.blockedIntro')}</p>

              {usage.resources.length > 0 && (
                <UsageSection
                  icon={Package}
                  title={t('resourceTypes.delete.resourcesLabel', {
                    count: usage.resources.length,
                  })}
                  items={usage.resources}
                />
              )}

              {usage.services.length > 0 && (
                <UsageSection
                  icon={Layers}
                  title={t('resourceTypes.delete.servicesLabel', {
                    count: usage.services.length,
                  })}
                  items={usage.services}
                />
              )}

              <p className="text-sm text-gray-500">{t('resourceTypes.delete.blockedHint')}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            {usage && !usage.canDelete ? t('common:buttons.close') : t('common:buttons.cancel')}
          </Button>
          {usage?.canDelete && (
            <Button
              variant="danger"
              loading={deleteResourceType.isPending}
              onClick={handleDelete}
            >
              {t('common:buttons.delete')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Sezione con l'elenco delle entità che bloccano l'eliminazione
// -----------------------------------------------------------------------------

interface UsageSectionProps {
  icon: React.ElementType;
  title: string;
  items: { id: string; name: string }[];
}

function UsageSection({ icon: Icon, title, items }: UsageSectionProps) {
  return (
    <div>
      <p className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
        <Icon className="w-3.5 h-3.5" />
        {title}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <Badge key={item.id} tone="amber">
            {item.name}
          </Badge>
        ))}
      </div>
    </div>
  );
}
